import TimelineIcon from '@mui/icons-material/Timeline'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import FullPageSpinner from '@/components/feedback/FullPageSpinner'
import CustomButton from '@/components/inputs/CustomButton'
import { BidUpdatesView } from '@/features/bidding/components/bidView/BidUpdatesView'
import { AFRR_MARKET_PROGRAMS, BidType, MFRR_MARKET_PROGRAMS } from '@/features/bidding/constants'
import { useBidUpdateOverviewQuery } from '@/features/bidding/hooks/useBidUpdateOverviewQuery'
import { buildBidDetailLink } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import getGroupedBidParamsFromUrl from '@/features/bidding/utils/groupedBidParams/getGroupedBidParamsFromUrl'

type Props = {
  bidType: BidType
}

export const BidHistoriesByTypeView: FC<Props> = ({ bidType }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const groupParams = getGroupedBidParamsFromUrl(location)
  const { bidUpdateOverview } = useBidUpdateOverviewQuery(
    {
      portfolioCode: groupParams?.portfolioCode,
      marketProgram: groupParams?.marketProgram,
      deliveryDay: groupParams?.deliveryDay,
      bidType,
    },
    { enabled: !!groupParams },
  )

  const isMfrrOrAfrr =
    groupParams &&
    (MFRR_MARKET_PROGRAMS.includes(groupParams.marketProgram) ||
      AFRR_MARKET_PROGRAMS.includes(groupParams?.marketProgram))
  const openBidResultButton =
    bidType === BidType.ENERGY && isMfrrOrAfrr ? (
      <CustomButton
        startIcon={<TimelineIcon />}
        variant="contained"
        onClick={() =>
          navigate(
            buildBidDetailLink({
              deliveryDay: groupParams.deliveryDay,
              portfolioCode: groupParams.portfolioCode,
              marketProgram: groupParams.marketProgram,
              bidType: BidType.ENERGY,
            }),
          )
        }
      >
        {t('component.page_header.bid.results.label')}
      </CustomButton>
    ) : undefined

  if (!groupParams || !bidUpdateOverview) {
    return <FullPageSpinner />
  }

  if (bidUpdateOverview.length === 0) {
    return <p>{t('bidding.results.no_bids')}</p>
  }

  return (
    <BidUpdatesView
      actionComponent={openBidResultButton}
      bidType={bidType}
      groupParams={groupParams}
      overview={bidUpdateOverview}
    />
  )
}
